/** @jsxImportSource @emotion/react */
import PropTypes from 'prop-types';
import { css } from '@emotion/react';

import Icon from 'components/Icon';
import { MEDIA_QUERIES } from 'styles/constants';
import { BLUE_FEEK, PURPLE_LIGHT, WHITE } from 'styles/colors';
import { ReactComponent as CheckIcon } from 'assets/icons/Check.svg';
import { ReactComponent as LightbulbIcon } from 'assets/icons/Lightbulb.svg';

const cardStyles = css`
  background: ${WHITE};
  border-radius: 20px;
  border: 1.5px solid #d8d8d8;
  padding: 57px;
  width: 599px;
  height: 756px;
  flex: 0 0 599px;
  position: relative;
  box-sizing: border-box;

  &.popular {
    border: 2px solid ${BLUE_FEEK};
  }

  ${MEDIA_QUERIES.smallScreen} {
    width: 100%;
    max-width: 599px;
    height: auto;
    flex: 0 0 auto;
  }

  ${MEDIA_QUERIES.tablet} {
    width: 100%;
    max-width: 520px;
    height: auto;
    flex: 0 0 auto;
    padding: 40px;

    .plan-name {
      font-size: 32px;
    }
    .plan-duration {
      font-size: 18px;
    }
    .plan-price-row .plan-price {
      font-size: 44px;
    }
    .plan-price-row .plan-currency {
      font-size: 18px;
    }
    .plan-includes-label {
      font-size: 18px;
    }
    .plan-feature {
      font-size: 16px;
    }
  }

  ${MEDIA_QUERIES.mobile} {
    width: 100%;
    height: auto;
    flex: 0 0 auto;
    padding: 28px 24px;
    border-radius: 16px;

    .plan-name {
      font-size: 28px;
    }
    .plan-duration {
      font-size: 16px;
    }
    .plan-price-row .plan-price {
      font-size: 38px;
    }
    .plan-price-row .plan-currency {
      font-size: 16px;
    }
    .plan-includes-label {
      font-size: 16px;
    }
    .plan-feature {
      font-size: 14px;
    }
    .popular-badge {
      font-size: 16px;
    }
  }

  .popular-badge {
    position: absolute;
    top: -16px;
    left: 50%;
    transform: translateX(-50%);
    background-color: ${BLUE_FEEK};
    color: #fff;
    leading-trim: both;
    text-edge: cap;
    font-family: Roobert, sans-serif;
    font-size: 25px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%;
    padding: 5px 18px;
    border-radius: 20px;
    white-space: nowrap;
  }

  .plan-name {
    color: #313033;
    leading-trim: both;
    text-edge: cap;
    font-family: Roobert, sans-serif;
    font-size: 45px;
    font-style: normal;
    font-weight: 700;
    line-height: 120%;
    margin: 0 0 4px;
  }

  .plan-duration {
    color: #5956f2;
    leading-trim: both;
    text-edge: cap;
    font-family: Roobert, sans-serif;
    font-size: 25px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%;
    margin: 0 0 16px;
  }

  .plan-price-row {
    display: flex;
    align-items: flex-end;
    gap: 6px;
    margin-bottom: 16px;

    .plan-price {
      color: #5956f2;
      leading-trim: both;
      text-edge: cap;
      font-family: Roobert, sans-serif;
      font-size: 60px;
      font-style: normal;
      font-weight: 700;
      line-height: 120%;
    }

    .plan-currency {
      color: #5956f2;
      leading-trim: both;
      text-edge: cap;
      font-family: Roobert, sans-serif;
      font-size: 25px;
      font-style: normal;
      font-weight: 400;
      line-height: 120%;
      margin-bottom: 7px;
    }
  }

  .plan-info {
    background-color: ${PURPLE_LIGHT};
    border-radius: 10px;
    padding: 10px 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 20px;

    .plan-info-icon {
      width: 24px;
      height: 24px;
      flex-shrink: 0;
      margin-left: 10px;
    }

    .plan-info-text {
      color: #313033;
      leading-trim: both;
      text-edge: cap;
      font-family: Roobert, sans-serif;
      font-size: 15px;
      font-style: normal;
      font-weight: 400;
      line-height: 140%;
      flex: 1;
    }
  }

  .plan-includes-label {
    color: #5956f2;
    leading-trim: both;
    text-edge: cap;
    font-family: Roobert, sans-serif;
    font-size: 25px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%;
    margin: 0 0 12px;
  }

  .plan-features {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;

    .plan-feature {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      color: #313033;
      leading-trim: both;
      text-edge: cap;
      font-family: Roobert, sans-serif;
      font-size: 20px;
      font-style: normal;
      font-weight: 400;
      line-height: 120%;

      b {
        font-weight: 700;
      }
    }
  }
`;

function PlanCard({
  name,
  duration,
  price,
  infoText,
  features,
  popular,
}) {
  return (
    <div css={cardStyles} className={popular ? 'popular' : undefined}>
      {popular && (
        <span className="popular-badge">⭐ Más popular</span>
      )}
      <p className="plan-name">{name}</p>
      <p className="plan-duration">Duración: {duration}</p>
      <div className="plan-price-row">
        <span className="plan-price">{price}</span>
        <span className="plan-currency">MXN</span>
      </div>
      <div className="plan-info">
        <LightbulbIcon className="plan-info-icon" />
        <p className="plan-info-text">{infoText}</p>
      </div>
      <p className="plan-includes-label">Incluye</p>
      <ul className="plan-features">
        {features.map((feature, i) => (
          <li key={i} className="plan-feature">
            <Icon icon={CheckIcon} color={BLUE_FEEK} size={20} />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

PlanCard.propTypes = {
  name: PropTypes.string.isRequired,
  duration: PropTypes.string.isRequired,
  price: PropTypes.string.isRequired,
  infoText: PropTypes.string.isRequired,
  features: PropTypes.arrayOf(PropTypes.node).isRequired,
  popular: PropTypes.bool,
};

PlanCard.defaultProps = {
  popular: false,
};

export default PlanCard;
